import { z } from 'zod';
import { RELATED_ENTITY_TYPES, type RelatedEntityType } from '../enums.js';
import { uuidSchema } from './common.js';

/**
 * Shapes shared by every CRM module.
 *
 * List responses embed the business manager's name rather than only an id. That keeps the
 * client from needing a second request — and, more importantly, from needing
 * `user:read` over the whole organization just to render a table, which is a
 * permission a sales rep should not have.
 */
export interface BusinessManagerRef {
  id: string;
  fullName: string;
  email: string;
}

/** A minimal pointer to another record, enough to render a link. */
export interface RecordRef {
  id: string;
  name: string;
}

/** A single uploaded file attached to a record — enough to show and download it. */
export interface AttachmentRef {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

/** How many files one account may carry, so a list cannot grow without bound. */
export const MAX_ACCOUNT_ATTACHMENTS = 50;
/** How many files one upload request may carry. */
export const MAX_ATTACHMENTS_PER_UPLOAD = 10;

export const businessManagerFilterSchema = z.union([
  uuidSchema,
  z.literal('me'),
  z.literal('unassigned'),
]);

export const relatedEntitySchema = z.object({
  type: z.enum(RELATED_ENTITY_TYPES),
  id: uuidSchema,
});
export type RelatedEntity = z.infer<typeof relatedEntitySchema>;

/** Maps a polymorphic reference onto the concrete foreign key column. */
export const RELATED_FOREIGN_KEY: Record<
  RelatedEntityType,
  'leadId' | 'accountId' | 'contactId' | 'dealId'
> = {
  LEAD: 'leadId',
  ACCOUNT: 'accountId',
  CONTACT: 'contactId',
  DEAL: 'dealId',
};

/** Bulk actions operate on an explicit id list, never on "everything matching". */
export const bulkIdsSchema = z.object({
  ids: z.array(uuidSchema).min(1, 'Select at least one record').max(200),
});
export type BulkIds = z.infer<typeof bulkIdsSchema>;

export const bulkAssignSchema = bulkIdsSchema.extend({
  businessManagerId: uuidSchema,
});
export type BulkAssign = z.infer<typeof bulkAssignSchema>;

export interface BulkResult {
  requested: number;
  affected: number;
  /** Ids the caller could not act on, so the UI can say so precisely. */
  skipped: string[];
}

export const addressSchema = z.object({
  street: z.string().trim().max(200).optional(),
  city: z.string().trim().max(120).optional(),
  state: z.string().trim().max(120).optional(),
  postalCode: z.string().trim().max(32).optional(),
  country: z.string().trim().max(120).optional(),
});
export type Address = z.infer<typeof addressSchema>;
